import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useJobPositionsService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { getEmployerJPState, useKv } from '@restworld/utils-common';
import { JPOnboardingType } from './types';
import { getValidationSchemaForStep } from './validation-schemas';
import {
  isOrgOnboardingModeAtom,
  orgOnboardingJPStepAtom,
} from '../organization-onboarding/use-organization-onboarding';

export type JPOnboardingSteps =
  | 'venue_selection'
  | 'venue_onboarding'
  | 'role'
  | 'skills'
  | 'other_requirements'
  | 'contract_and_shifts'
  | 'salary'
  | 'benefits'
  | 'additional_notes'
  | 'ai_autogeneration'
  | 'review'
  | 'company_and_payment'
  | 'success';

// Sub-view types for the unified company_and_payment step
export type CompanyAndPaymentSubView =
  | 'loading'
  | 'product_card'
  | 'choose_company_type'
  | 'select_company'
  | 'insert_company'
  | 'add_payment'
  | 'show_payments'
  | 'confirm';

// Define the ordered list of steps for navigation (main flow, ends at review)
export const ORDERED_STEPS: JPOnboardingSteps[] = [
  'venue_selection',
  'role',
  'skills',
  'other_requirements',
  'contract_and_shifts',
  'salary',
  'benefits',
  'additional_notes',
  'ai_autogeneration',
  'review',
];

// Steps that should be skipped for non-PPU users (but shown for new org onboarding users)
export const PPU_ONLY_STEPS: JPOnboardingSteps[] = ['additional_notes'];

// Steps that can be accessed via explicit goToStep() calls only
export const SIDE_STEPS: JPOnboardingSteps[] = ['venue_onboarding'];

// Define which steps should NOT be counted in progress
export const UNCOUNTED_STEPS: JPOnboardingSteps[] = [
  'venue_selection',
  'venue_onboarding',
  'additional_notes',
  'ai_autogeneration',
  'review',
  'company_and_payment',
  'success',
];

// Get only the steps that should be counted
export const COUNTED_STEPS = ORDERED_STEPS.filter((step) => !UNCOUNTED_STEPS.includes(step));

// All valid steps (including side steps and post-review flow)
export const ALL_VALID_STEPS: JPOnboardingSteps[] = [...ORDERED_STEPS, ...SIDE_STEPS, 'company_and_payment', 'success'];

const stepResetState = 'venue_selection';
export const jpOnboardingStepsAtom = atom<JPOnboardingSteps>(stepResetState);

// // TODO: DELETE THIS HARDCODED JP ID AFTER DEV
// const jpResetValue = '345edad4-42bc-4f14-ad69-25dbfcda410e';
const jpResetValue = '';

// Atom to store the created JP ID
export const onboardingJobPositionIdAtom = atom<string>(jpResetValue);

// Atom to track AI autogeneration error state
export const aiAutoGenerationErrorAtom = atom<boolean>(false);

// Atom to show the preview modal of the JP
export const jobPreviewModalOpenAtom = atom<boolean>(false);

// Atom to track which job positions have had their preview modal auto-opened
// Maps job ID to boolean (true = preview has been shown for this job)
export const jobPreviewSeenByJobIdAtom = atom<Record<string, boolean>>({});

// LocalStorage key used to persist the preview-seen map across page refreshes
export const JOB_PREVIEW_SEEN_STORAGE_KEY = 'jp-onboarding-preview-seen';

// Type for dependent step information
export interface DependentStepInfo {
  step: JPOnboardingSteps;
  fields: string[];
}

// Atom to store dependent steps that need review after field changes
export const dependentStepsAtom = atom<DependentStepInfo[]>([]);

// Type for updated payment method information
export interface UpdatedPaymentMethodInfo {
  customerId: string;
  paymentMethodId: string;
}

// Atom to track if the default payment method was changed (stores customer ID and payment method ID)
export const updatedDefaultPaymentMethodAtom = atom(null as UpdatedPaymentMethodInfo | null);

// Atom to track the current sub-view within the company_and_payment step
export const companyAndPaymentSubViewAtom = atom<CompanyAndPaymentSubView>('loading');

// Atom to store the payment readiness status from the backend
export type PaymentReadinessStatus = 'ready' | 'missing_company' | 'missing_payment_method' | 'unpaid_invoices' | null;
export const paymentReadinessStatusAtom = atom(null as PaymentReadinessStatus);

// KV key prefix for storing JP onboarding step
const JP_ONBOARDING_KV_PREFIX = 'jp-onboarding-step';

/**
 * Hook to initialize JP Onboarding from URL params and KV store
 * Should be called once when the onboarding form is first mounted
 */
export const useJPOnboardingInitialization = (resetForm: (data: Partial<JPOnboardingType>) => void) => {
  const router = useRouter();
  const { getKV } = useKv();
  const jobPositionsService = useJobPositionsService();
  const setCurrentStep = useSetAtom(jpOnboardingStepsAtom);
  const setJpId = useSetAtom(onboardingJobPositionIdAtom);
  const { goToStep } = useJPOnboardingSteps();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const initRef = useRef(false);

  // Reset onboarding to initial state
  const resetOnboardingState = useCallback(() => {
    setJpId(jpResetValue);
    goToStep(stepResetState);
  }, [setJpId, goToStep]);

  useEffect(() => {
    // Prevent multiple initializations
    if (initRef.current || isInitialized) return;
    initRef.current = true;

    const initializeFromUrlAndKV = async () => {
      const { job_position_id, step } = router.query as {
        job_position_id?: string;
        step?: JPOnboardingSteps;
      };

      // Helper: redirect to management page if JP is already online
      const redirectIfOnline = (jpData: { status_key?: string }, jpId: string): boolean => {
        if (getEmployerJPState(jpData.status_key) === 'open') {
          router.push(`/employer/job-positions/${jpId}`);
          setIsInitialized(true);
          return true;
        }
        return false;
      };

      // Case 1: Both job_position_id and step exist in URL
      if (job_position_id && step && ALL_VALID_STEPS.includes(step)) {
        try {
          const response = await jobPositionsService.showJobPosition(job_position_id);
          if (response.data) {
            if (redirectIfOnline(response.data, job_position_id)) return;
            // Set the JP ID and step from URL
            setJpId(job_position_id);
            setCurrentStep(step);
            // Reset form with fetched data
            resetForm(response.data);
            setIsInitialized(true);
            return;
          }
        } catch (error) {
          // If fetch fails (404 or other error), start fresh
          // eslint-disable-next-line no-console
          console.error('Error fetching job position from URL params:', error);
          resetOnboardingState();
          setIsInitialized(true);
          return;
        }
      }

      // Case 2: Only job_position_id exists in URL
      if (job_position_id && !step) {
        try {
          const response = await jobPositionsService.showJobPosition(job_position_id);
          if (response.data) {
            if (redirectIfOnline(response.data, job_position_id)) return;
            // Set the JP ID from URL
            setJpId(job_position_id);
            // Reset form with fetched data
            resetForm(response.data);

            // Try to get step from KV store
            const kvKey = `${JP_ONBOARDING_KV_PREFIX}-${job_position_id}`;
            const kvResult = await getKV(kvKey);

            if (kvResult?.value && ALL_VALID_STEPS.includes(kvResult.value as JPOnboardingSteps)) {
              // Navigate to the step from KV store
              setCurrentStep(kvResult.value as JPOnboardingSteps);
              // Update URL with the step
              router.replace(
                {
                  pathname: router.pathname,
                  query: { job_position_id, step: kvResult.value },
                },
                undefined,
                { shallow: true }
              );
            } else {
              // No step in KV, start from venue_selection with the loaded data
              setCurrentStep(stepResetState);
              router.replace(
                {
                  pathname: router.pathname,
                  query: { job_position_id, step: stepResetState },
                },
                undefined,
                { shallow: true }
              );
            }
            setIsInitialized(true);
            return;
          }
        } catch (error) {
          // If fetch fails, start fresh
          // eslint-disable-next-line no-console
          console.error('Error fetching job position:', error);
          resetOnboardingState();
          setIsInitialized(true);
          return;
        }
      }

      // Case 3: No URL params - start fresh
      // Reset to initial state (this handles clean start)
      resetOnboardingState();
      setIsInitialized(true);
    };

    // Only run when router is ready
    if (router.isReady) {
      initializeFromUrlAndKV();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    router.isReady,
    router.query,
    jobPositionsService,
    setJpId,
    setCurrentStep,
    resetForm,
    getKV,
    isInitialized,
    setIsInitialized,
    resetOnboardingState,
  ]);

  // Sync atom state with URL when browser back/forward navigation occurs
  // This pattern is based on libs/utils-common/src/hooks/wizard/wizard.hooks.tsx
  // When URL query changes (e.g., via browser back/forward), sync the atom state
  useEffect(() => {
    if (!router.isReady || !isInitialized) return;

    const { step: stepFromUrl, job_position_id: jpIdFromUrl } = router.query as {
      step?: JPOnboardingSteps;
      job_position_id?: string;
    };

    // Sync step if URL has a valid step, otherwise reset to initial state
    if (stepFromUrl && ALL_VALID_STEPS.includes(stepFromUrl)) {
      setCurrentStep(stepFromUrl);
    }

    // Sync JP ID if URL has a different value
    if (jpIdFromUrl) {
      setJpId(jpIdFromUrl);
    }
  }, [router.isReady, router.query, isInitialized, setCurrentStep, setJpId]);

  return { isInitialized };
};

/**
 * Hook for managing JP Onboarding form step state and progress
 * Provides utility functions for step transitions and progress tracking
 */
export const useJPOnboardingSteps = () => {
  const { t } = useTranslation('jp-onboarding');
  const router = useRouter();
  const { setKV } = useKv();
  const currentStep = useAtomValue(jpOnboardingStepsAtom);
  const aiAutoGenerationHasError = useAtomValue(aiAutoGenerationErrorAtom);
  const setCurrentStep = useSetAtom(jpOnboardingStepsAtom);
  const jpId = useAtomValue(onboardingJobPositionIdAtom);
  const { user } = useAuth();
  const isPPU = user?.using_ppu_business_model === true;
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);

  const currentStepIndex = ORDERED_STEPS.indexOf(currentStep);

  // Check if a step should be skipped based on user type
  // PPU_ONLY_STEPS are shown for PPU users AND organization onboarding users
  const shouldSkipStep = useCallback(
    (step: JPOnboardingSteps) => !isPPU && !isOrgOnboarding && PPU_ONLY_STEPS.includes(step),
    [isPPU, isOrgOnboarding]
  );

  // PPU users and org onboarding users see PPU-only steps in the progress bar; non-PPU users don't
  const effectiveUncountedSteps = useMemo(
    () => (isPPU || isOrgOnboarding ? UNCOUNTED_STEPS.filter((s) => !PPU_ONLY_STEPS.includes(s)) : UNCOUNTED_STEPS),
    [isPPU, isOrgOnboarding]
  );

  // Total steps that should be counted (PPU-only steps count for PPU users and org onboarding users)
  const totalSteps = useMemo(
    () => ORDERED_STEPS.filter((s) => !effectiveUncountedSteps.includes(s)).length,
    [effectiveUncountedSteps]
  );

  // Helper function to update URL with shallow routing
  const updateUrlWithState = useCallback(
    (step: JPOnboardingSteps, overrideJpId?: string) => {
      const effectiveJpId = overrideJpId ?? jpId;
      const query: { step: string; job_position_id?: string } = { step };
      if (effectiveJpId) {
        query.job_position_id = effectiveJpId;
      }
      router.push(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true }
      );
    },
    [router, jpId]
  );

  // Helper function to store step in KV store
  const storeStepInKV = useCallback(
    async (step: JPOnboardingSteps, overrideJpId?: string) => {
      const effectiveJpId = overrideJpId ?? jpId;
      if (!effectiveJpId) return;
      const kvKey = `${JP_ONBOARDING_KV_PREFIX}-${effectiveJpId}`;
      try {
        await setKV({ key: kvKey, value: step, type: 'jp_onboarding_step' });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error storing step in KV:', error);
      }
    },
    [setKV, jpId]
  );

  // Check if we're in org onboarding mode
  const isOrgOnboardingMode = useAtomValue(isOrgOnboardingModeAtom);
  const setOrgJPStep = useSetAtom(orgOnboardingJPStepAtom);

  // Apply side effects: update URL and KV store
  // overrideJpId can be passed when the jpId atom hasn't been updated yet (e.g., after JP creation)
  // In org onboarding mode, also persist JP step to localStorage atom for refresh recovery.
  // URL is updated in org format: ?step=job_position_creation&sub_step=<jp_step>
  // storeStepInKV is a no-op in org onboarding (no jpId → early return inside the function).
  const applySideEffects = useCallback(
    (step: JPOnboardingSteps, overrideJpId?: string) => {
      if (isOrgOnboardingMode) {
        // Persist JP sub-step so we can restore on refresh
        setOrgJPStep(step);
        // Update URL with org onboarding format (skip if already matches to avoid duplicate history entries)
        const currentQuery = router.query as Record<string, string | undefined>;
        if (currentQuery.step !== 'job_position_creation' || currentQuery.sub_step !== step) {
          router.push(
            { pathname: router.pathname, query: { step: 'job_position_creation', sub_step: step } },
            undefined,
            { shallow: true }
          );
        }
        return;
      }
      updateUrlWithState(step, overrideJpId);
      storeStepInKV(step, overrideJpId);
    },
    [updateUrlWithState, storeStepInKV, isOrgOnboardingMode, setOrgJPStep, router]
  );

  // Navigate to next step with side effects (main flow only)
  // Skips PPU-only steps for non-PPU users
  const goToNextStep = useCallback(() => {
    let nextIndex = currentStepIndex + 1;
    while (nextIndex < ORDERED_STEPS.length && shouldSkipStep(ORDERED_STEPS[nextIndex])) {
      nextIndex++;
    }
    if (nextIndex < ORDERED_STEPS.length) {
      const nextStep = ORDERED_STEPS[nextIndex];
      setCurrentStep(nextStep);
      applySideEffects(nextStep);
    }
  }, [currentStepIndex, setCurrentStep, applySideEffects, shouldSkipStep]);

  // Navigate to previous step with side effects
  // Skips PPU-only steps for non-PPU users
  const goToPreviousStep = useCallback(() => {
    // From company_and_payment, go back to review
    if (currentStep === 'company_and_payment') {
      setCurrentStep('review');
      applySideEffects('review');
      return;
    }

    let prevIndex = currentStepIndex - 1;
    while (prevIndex >= 0 && shouldSkipStep(ORDERED_STEPS[prevIndex])) {
      prevIndex--;
    }
    if (prevIndex >= 0) {
      const prevStep = ORDERED_STEPS[prevIndex];
      setCurrentStep(prevStep);
      applySideEffects(prevStep);
    } else {
      setCurrentStep(stepResetState);
      applySideEffects(stepResetState);
    }
  }, [currentStep, currentStepIndex, setCurrentStep, applySideEffects, shouldSkipStep]);

  // Navigate to a specific step with side effects
  const goToStep = useCallback(
    (step: JPOnboardingSteps) => {
      setCurrentStep(step);
      // Updates URL and KV store
      applySideEffects(step);
    },
    [setCurrentStep, applySideEffects]
  );

  // Get progress information
  const getProgressInfo = useCallback(() => {
    // Check if current step should be counted (using effective set based on user type)
    const isUncountedStep = effectiveUncountedSteps.includes(currentStep);

    // Calculate the position within counted steps
    const countedStepsBeforeCurrent = ORDERED_STEPS.slice(0, currentStepIndex).filter(
      (step) => !effectiveUncountedSteps.includes(step)
    ).length;

    // For uncounted steps, show 0; for counted steps, show their position (1-indexed)
    const adjustedStepIndex = isUncountedStep ? 0 : countedStepsBeforeCurrent + 1;

    // Calculate percentage based on counted steps only
    const percentageCompleted = isUncountedStep ? 0 : Math.round((adjustedStepIndex / totalSteps) * 100);

    return {
      currentStep,
      currentStepIndex: adjustedStepIndex,
      totalSteps,
      percentageCompleted,
    };
  }, [currentStep, currentStepIndex, totalSteps, effectiveUncountedSteps]);
  // Get validation schema for current step
  const getCurrentStepValidationSchema = useCallback(() => {
    return getValidationSchemaForStep(currentStep, t);
  }, [currentStep, t]);

  // Determine if header should be shown
  const shouldShowHeader = useMemo(() => {
    // Hide header during AI autogeneration loading (only show if there's an error)
    if (currentStep === 'ai_autogeneration') {
      return aiAutoGenerationHasError;
    }
    // Hide header on success step (clean full-screen experience)
    if (currentStep === 'success') {
      return false;
    }
    return true;
  }, [currentStep, aiAutoGenerationHasError]);

  // Determine if footer should be shown
  const shouldShowFooter = useMemo(() => {
    // Hide footer during AI autogeneration loading (only show if there's an error)
    if (currentStep === 'ai_autogeneration') {
      return aiAutoGenerationHasError;
    }
    // Hide footer during venue onboarding (has its own footer)
    if (currentStep === 'venue_onboarding') {
      return false;
    }
    // Hide footer on success step (has its own CTA button)
    if (currentStep === 'success') {
      return false;
    }
    return true;
  }, [currentStep, aiAutoGenerationHasError]);

  return {
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    getProgressInfo,
    getCurrentStepValidationSchema,
    shouldShowHeader,
    shouldShowFooter,
    effectiveUncountedSteps,
    isFirstStep: currentStep === ORDERED_STEPS[0],
    isLastStep: currentStep === ORDERED_STEPS[ORDERED_STEPS.length - 1],
    applySideEffects,
  };
};

/**
 * Hook to handle JP form submission (create/update)
 * This hook manages the API calls for creating and updating the job position
 */
export const useJPOnboardingSubmit = () => {
  const currentStep = useAtomValue(jpOnboardingStepsAtom);
  const setCurrentStep = useSetAtom(jpOnboardingStepsAtom);
  const [jpId, setJpId] = useAtom(onboardingJobPositionIdAtom);
  const jobPositionsService = useJobPositionsService();
  const { applySideEffects } = useJPOnboardingSteps();
  const isOrgOnboardingMode = useAtomValue(isOrgOnboardingModeAtom);

  // Create JP using the service
  const createJP = useCallback(
    async (data: Partial<JPOnboardingType>) => {
      const response = await jobPositionsService.createJobPosition({ params: data });
      const newJpId = response.data.id;
      setJpId(newJpId);
      // Update URL and KV with the new JP ID and next step
      const nextStepIndex = ORDERED_STEPS.indexOf(currentStep) + 1;
      const nextStep = ORDERED_STEPS[nextStepIndex] || currentStep;
      // Update the step atom
      setCurrentStep(nextStep);
      // Apply side effects with the new JP ID (override since atom hasn't updated yet)
      applySideEffects(nextStep, newJpId);
      return newJpId;
    },
    [jobPositionsService, setJpId, currentStep, setCurrentStep, applySideEffects]
  );

  // Update JP using the service
  const updateJP = useCallback(
    async (data: Partial<JPOnboardingType>) => {
      if (!jpId) {
        throw new Error('Cannot update JP: JP ID is not defined');
      }

      await jobPositionsService.updateJobPosition({
        jobPositionId: jpId,
        params: data,
      });
    },
    [jobPositionsService, jpId]
  );

  // Determine if we should create or update
  // In org onboarding mode, never create (data stored locally until bulk submission)
  const shouldCreateJP = useMemo(
    () => !isOrgOnboardingMode && currentStep === 'role' && (!jpId || jpId === ''),
    [currentStep, jpId, isOrgOnboardingMode]
  );

  // Request to publish JP using the service
  // In org onboarding mode, this is a no-op (publication happens after entity creation)

  const requestToPublishJP = useCallback(async () => {
    if (isOrgOnboardingMode) return; // Skip in org onboarding mode
    if (!jpId) {
      throw new Error('Cannot request to publish JP: JP ID is not defined');
    }

    await jobPositionsService.requestToPublishJobPosition({
      jobPositionId: jpId,
    });
  }, [jobPositionsService, jpId, isOrgOnboardingMode]);

  /**
   * Validate ad content via AI.
   *
   * Prefers jpId when available (works in both standard and org onboarding modes).
   * Falls back to raw jpData only when no jpId exists (initial org onboarding before entity creation).
   */
  const validateAdJP = useCallback(
    async (jpData?: Record<string, unknown>, rateLimitKey?: string) => {
      // Prefer ID-based validation when jpId is available
      if (jpId) {
        const response = await jobPositionsService.validateAd({ jobPositionId: jpId });
        return response.data;
      }

      // Fallback: validate from raw data (org onboarding without jpId)
      if (jpData) {
        const response = await jobPositionsService.validateAd({ jpData, rateLimitKey });
        return response.data;
      }

      throw new Error('Cannot validate ad: JP ID is not defined and no jpData provided');
    },
    [jobPositionsService, jpId]
  );

  // Put JP online (PPU flow - company_and_payment confirm step)
  const goOnlineJP = useCallback(async () => {
    if (!jpId) {
      throw new Error('Cannot go online: JP ID is not defined');
    }

    await jobPositionsService.goOnline({ jobPositionId: jpId });
  }, [jobPositionsService, jpId]);

  // Check payment readiness (PPU flow - company_and_payment step)
  const checkPaymentReadinessJP = useCallback(async () => {
    const response = await jobPositionsService.checkPaymentReadiness();
    return response.data;
  }, [jobPositionsService]);

  // Submit handler - decides whether to create or update
  // In org onboarding mode, this is a no-op (data persistence is handled by applySideEffects)
  const submitJP = useCallback(
    async (data: Partial<JPOnboardingType>) => {
      if (!isOrgOnboardingMode && shouldCreateJP) {
        return await createJP(data);
      }
      if (jpId && jpId !== '') {
        await updateJP(data);
        return jpId;
      }
      return null;
    },
    [shouldCreateJP, createJP, updateJP, jpId, isOrgOnboardingMode]
  );

  return {
    submitJP,
    requestToPublishJP,
    validateAdJP,
    goOnlineJP,
    checkPaymentReadinessJP,
    shouldCreateJP,
  };
};
